import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import type { CheckedState } from '@radix-ui/react-checkbox'
import { ChevronDown, ChevronRight, Search } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { Checkbox } from '@/components/ui/checkbox'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  useSetStaffRolePermissions,
  useStaffPermissions,
  useStaffRoles,
} from '@/features/platformAdmin/queries'
import type { StaffRole } from '@/features/platformAdmin/types'
import { groupAdminPermissions } from '@/features/platformAdmin/adminPermissionGroups'

const errMsg = (err: unknown, fallback = 'Failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function StaffRolesTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const rolesQ = useStaffRoles()
  const permsQ = useStaffPermissions()
  const setPerms = useSetStaffRolePermissions()

  const canEdit = hasPermission('admin.staff.roles')
  const [editing, setEditing] = useState<StaffRole | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [permSearch, setPermSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Hooks must run on every render in the same order, so derive memos from
  // the (possibly empty) data BEFORE any early return. permsQ.data may be
  // undefined while loading; we treat that as "no permissions yet" and the
  // grouped result is just an empty array.
  const allGrouped = useMemo(
    () => groupAdminPermissions(permsQ.data?.permissions ?? []),
    [permsQ.data?.permissions],
  )

  const filteredGroups = useMemo(() => {
    const q = permSearch.trim().toLowerCase()
    if (!q) return allGrouped
    return allGrouped
      .map((g) => ({
        ...g,
        permissions: g.permissions.filter(
          (p) =>
            p.code.toLowerCase().includes(q) ||
            (p.description ?? '').toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.permissions.length > 0)
  }, [allGrouped, permSearch])

  const visibleGroupIds = useMemo(
    () => filteredGroups.map((g) => g.group.id),
    [filteredGroups],
  )
  const expandedVisibleCount = useMemo(
    () => visibleGroupIds.reduce((n, id) => (expanded.has(id) ? n + 1 : n), 0),
    [expanded, visibleGroupIds],
  )
  const allExpandedState: CheckedState =
    visibleGroupIds.length === 0
      ? false
      : expandedVisibleCount === visibleGroupIds.length
        ? true
        : expandedVisibleCount === 0
          ? false
          : 'indeterminate'

  const visiblePermCodes = useMemo(
    () => filteredGroups.flatMap((g) => g.permissions.map((p) => p.code)),
    [filteredGroups],
  )
  const visibleSelectedCount = useMemo(
    () => visiblePermCodes.reduce((n, c) => (selected.has(c) ? n + 1 : n), 0),
    [selected, visiblePermCodes],
  )
  const allSelectedState: CheckedState =
    visiblePermCodes.length === 0
      ? false
      : visibleSelectedCount === visiblePermCodes.length
        ? true
        : visibleSelectedCount === 0
          ? false
          : 'indeterminate'

  if (rolesQ.isLoading || permsQ.isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  const roles = rolesQ.data?.roles ?? []
  const totalPerms = permsQ.data?.permissions?.length ?? 0

  const openEdit = (role: StaffRole) => {
    setEditing(role)
    setSelected(new Set(role.permissions))
    setError(null)
    setPermSearch('')
    setExpanded(new Set())
  }

  const close = () => {
    setEditing(null)
    setError(null)
    setPermSearch('')
    setExpanded(new Set())
  }

  const togglePerm = (code: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })

  const toggleGroup = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const groupSelectionState = (codes: string[]): 'all' | 'some' | 'none' => {
    if (codes.length === 0) return 'none'
    let on = 0
    for (const c of codes) if (selected.has(c)) on++
    if (on === 0) return 'none'
    if (on === codes.length) return 'all'
    return 'some'
  }

  const setGroup = (codes: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      for (const c of codes) {
        if (on) next.add(c)
        else next.delete(c)
      }
      return next
    })

  const toggleExpandAll = (next: CheckedState) => {
    if (next === true) {
      setExpanded((prev) => {
        const out = new Set(prev)
        for (const id of visibleGroupIds) out.add(id)
        return out
      })
    } else {
      setExpanded((prev) => {
        const out = new Set(prev)
        for (const id of visibleGroupIds) out.delete(id)
        return out
      })
    }
  }

  const toggleSelectAll = (next: CheckedState) => {
    if (next === true) {
      setSelected((prev) => {
        const out = new Set(prev)
        for (const c of visiblePermCodes) out.add(c)
        return out
      })
    } else {
      setSelected((prev) => {
        const out = new Set(prev)
        for (const c of visiblePermCodes) out.delete(c)
        return out
      })
    }
  }

  const submit = async () => {
    if (!editing) return
    setError(null)
    try {
      await setPerms.mutateAsync({ roleId: editing.id, permissions: Array.from(selected) })
      toast({ title: 'Permissions updated.' })
      close()
    } catch (err) {
      setError(errMsg(err))
    }
  }

  const columns: ColumnDef<StaffRole>[] = [
    {
      accessorKey: 'name',
      header: 'Role',
      cell: ({ row }) => <span className="font-semibold">{row.original.name}</span>,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{row.original.description ?? '-'}</span>
      ),
    },
    { accessorKey: 'member_count', header: 'Members' },
    {
      id: 'permCount',
      header: 'Permissions',
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          {row.original.permissions.length}
          <span className="text-muted-foreground"> / {totalPerms}</span>
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        row.original.name === 'super_admin' ? (
          <Badge variant="secondary">all</Badge>
        ) : canEdit ? (
          <Button size="sm" variant="ghost" onClick={() => openEdit(row.original)}>
            Edit
          </Button>
        ) : null,
    },
  ]

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-0">
          <DataTable columns={columns} rows={roles} />
        </CardContent>
      </Card>

      <Modal
        open={!!editing}
        onOpenChange={(v) => !v && close()}
        title={editing ? `Edit: ${editing.name}` : ''}
        className="sm:max-w-3xl"
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={setPerms.isPending}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-sm font-medium">Permissions</label>
              <span className="text-xs text-muted-foreground">
                {visibleSelectedCount} of {visiblePermCodes.length} selected
                {permSearch.trim() ? (
                  <span className="ml-1 text-muted-foreground/70">
                    (filtered from {totalPerms})
                  </span>
                ) : null}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <div className="relative min-w-[220px] flex-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  placeholder="Search permissions by name or description…"
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.currentTarget.value)}
                  className="pl-9"
                />
              </div>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <Checkbox
                  checked={allSelectedState}
                  onCheckedChange={toggleSelectAll}
                  disabled={visiblePermCodes.length === 0}
                  aria-label="Select all permissions"
                />
                Select all
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <Checkbox
                  checked={allExpandedState}
                  onCheckedChange={toggleExpandAll}
                  disabled={visibleGroupIds.length === 0}
                  aria-label="Expand all permission groups"
                />
                Expand all
              </label>
            </div>
            <div className="mt-2 max-h-[55vh] space-y-1 overflow-y-auto rounded-md border p-2">
              {filteredGroups.length === 0 ? (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                  No permissions match &ldquo;{permSearch}&rdquo;.
                </p>
              ) : (
                filteredGroups.map((g) => {
                  const codes = g.permissions.map((p) => p.code)
                  const state = groupSelectionState(codes)
                  // Auto-expand groups with active filter results so the user
                  // can see what matched without having to expand each one.
                  const isExpanded = expanded.has(g.group.id) || permSearch.trim().length > 0
                  return (
                    <div key={g.group.id} className="rounded-md border bg-muted/30">
                      <div className="flex items-center gap-2 px-2 py-1.5">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => toggleGroup(g.group.id)}
                          className="h-auto flex-1 justify-start gap-1.5 p-1 text-left font-normal hover:bg-transparent"
                          aria-expanded={isExpanded}
                          aria-controls={`staff-group-${g.group.id}`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
                          ) : (
                            <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                          )}
                          <span className="text-sm font-medium">{g.group.label}</span>
                          <Badge variant="secondary" className="text-[10px]">
                            {g.permissions.filter((p) => selected.has(p.code)).length} / {g.permissions.length}
                          </Badge>
                        </Button>
                        <label
                          className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={state === 'all' ? true : state === 'some' ? 'indeterminate' : false}
                            onCheckedChange={() => setGroup(codes, state !== 'all')}
                            aria-label={`Select all ${g.group.label}`}
                          />
                          Select all
                        </label>
                      </div>
                      {g.group.description ? (
                        <p
                          className={cn(
                            'px-2 pb-1 text-[11px] text-muted-foreground',
                            isExpanded ? '' : 'hidden',
                          )}
                        >
                          {g.group.description}
                        </p>
                      ) : null}
                      <div
                        id={`staff-group-${g.group.id}`}
                        className={cn(
                          'space-y-0.5 border-t bg-background px-2 py-1',
                          isExpanded ? '' : 'hidden',
                        )}
                      >
                        {g.permissions.map((p) => (
                          <label
                            key={p.code}
                            className="flex items-start gap-2 rounded-sm p-1 hover:bg-muted/50"
                          >
                            <Checkbox
                              checked={selected.has(p.code)}
                              onCheckedChange={() => togglePerm(p.code)}
                              className="mt-0.5"
                            />
                            <span className="text-sm leading-tight">
                              {p.description ? (
                                <span>{p.description}</span>
                              ) : (
                                <span className="text-muted-foreground">No description.</span>
                              )}
                              <code className="ml-1 text-[10px] text-muted-foreground">{p.code}</code>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </Modal>
    </div>
  )
}
